<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';

import { getFocusableElements } from '@sg-core/resources/assets/js/utils';

import PhxsgDomContent from './phxsg-dom-content.vue';
import PhxsgTabContent from './phxsg-tab-content.vue';
import PhxsgTabsWrapper from './phxsg-tabs-wrapper.vue';

import type { PhxsgTabsProps } from './tabs.types';

const props = withDefaults(defineProps<PhxsgTabsProps>(), {
  rootElement: null,
  value: null,
  teleportTarget: null,
  cssEnv: () => ({}),
  orientation: 'horizontal',
  layout: 'stretch',
  tabButtonClasses: () => [],
  responsiveLayouts: null,
  tabButtonsIcon: '',
  showIndicator: true,
  transitionName: 'phxsg-tabs__panel-',
  transitionMode: 'default',
  contentOffset: 40,
  indicatorThickness: 2,
  startSlotNodes: () => [],
  endSlotNodes: () => [],
});

const emit = defineEmits<{
  change: [id: string, button: EventTarget | null];
}>();

const currentTabContent = ref<HTMLElement | null>(null);
const viewportWidth = ref(typeof window === 'undefined' ? 0 : window.innerWidth);
const generatedId = useId();
const instanceId = computed(() => props.rootElement?.id || `tabs-${generatedId}`);

function resolveCurrentValue(value: string | null | undefined) {
  const matchingPanel = props.panels.find((panel) => panel.id === value && !panel.disabled);

  if (matchingPanel) {
    return matchingPanel.id;
  }

  return props.panels.find((panel) => !panel.disabled)?.id ?? null;
}

const currentValue = ref<string | null>(resolveCurrentValue(props.value));

function updateViewportWidth() {
  viewportWidth.value = window.innerWidth;
}

function getEffectiveLayoutValue() {
  let effectiveLayout = props.layout;

  // Keep the computed layout reactive to viewport changes like the historical Vue component.
  void viewportWidth.value;

  for (const rule of props.responsiveLayouts ?? []) {
    if (!rule.from) {
      effectiveLayout = rule.layout;

      continue;
    }

    const cssEnvValue = props.cssEnv[`--breakpoint-${rule.from}`]?.trim();
    const breakpointSource =
      cssEnvValue || getComputedStyle(document.documentElement).getPropertyValue(`--ref-breakpoint-${rule.from}`);
    const breakpointValue = parseInt(breakpointSource, 10);

    if (!Number.isNaN(breakpointValue) && window.matchMedia(`screen and (min-width: ${breakpointValue}px)`).matches) {
      effectiveLayout = rule.layout;
    }
  }

  return effectiveLayout;
}

const effectiveLayout = computed(() => getEffectiveLayoutValue());
const currentPanel = computed(() => props.panels.find((panel) => panel.id === currentValue.value) ?? null);

onMounted(() => {
  updateViewportWidth();
  window.addEventListener('resize', updateViewportWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateViewportWidth);
});

watch(
  () => props.value,
  (nextValue) => {
    currentValue.value = resolveCurrentValue(nextValue);
  },
  { immediate: true },
);

watch(
  () => props.panels,
  () => {
    currentValue.value = resolveCurrentValue(currentValue.value);
  },
  { deep: true },
);

watch(
  effectiveLayout,
  (nextLayout) => {
    if (!props.rootElement) {
      return;
    }

    for (const layoutName of ['stretch', 'start', 'expandable']) {
      props.rootElement.classList.toggle(`phxsg-tabs--${layoutName}`, layoutName === nextLayout);
    }
  },
  { immediate: true },
);

function onTabClick({ id }: { id: string }, event: MouseEvent | KeyboardEvent) {
  emit('change', id, event.target);
}

function onTabContentChange(element: HTMLElement) {
  currentTabContent.value = element;
}

function onTabContentFocus(event: KeyboardEvent) {
  if (!currentTabContent.value) {
    return;
  }

  const elements = getFocusableElements(currentTabContent.value);

  if (elements[0]) {
    event.preventDefault();
    elements[0].focus();
  }
}
</script>

<template>
  <PhxsgTabsWrapper
    :instance-id="instanceId"
    :tabs="props.panels"
    :tab-buttons-icon="props.tabButtonsIcon"
    :value="currentValue ?? ''"
    :layout="effectiveLayout"
    :orientation="props.orientation"
    :tab-button-classes="props.tabButtonClasses"
    :show-indicator="props.showIndicator"
    :indicator-thickness="props.indicatorThickness"
    :root-element="props.rootElement"
    @tab-click="onTabClick"
    @tab-content-focus="onTabContentFocus"
  >
    <template v-if="props.startSlotNodes.length > 0" #start>
      <div class="phxsg-tabs__slot-content">
        <PhxsgDomContent :nodes="props.startSlotNodes" />
      </div>
    </template>

    <template v-if="props.endSlotNodes.length > 0" #end>
      <div class="phxsg-tabs__slot-content">
        <PhxsgDomContent :nodes="props.endSlotNodes" />
      </div>
    </template>
  </PhxsgTabsWrapper>

  <PhxsgTabContent
    :instance-id="instanceId"
    :panels="props.panels"
    :panel="currentPanel"
    :layout="effectiveLayout"
    :teleport-target="props.teleportTarget"
    :orientation="props.orientation"
    :transition-name="props.transitionName"
    :transition-mode="props.transitionMode"
    :content-offset="props.contentOffset"
    @tab-content-change="onTabContentChange"
  />
</template>
